import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Panel } from "@/components/KpiCard";
import { useAuth } from "@/context/AuthContext";
import { api, download, errMsg } from "@/lib/api";

const taille = (o) => (o > 1024 ** 2 ? `${(o / 1024 ** 2).toFixed(1).replace(".", ",")} Mo` : `${Math.ceil(o / 1024)} Ko`);

const Copies = ({ copies, onChange }) => {
  const supprimer = async (nom) => {
    if (!window.confirm(`Supprimer la copie « ${nom} » ?`)) { return; }
    try { await api.delete(`/local/copies/${encodeURIComponent(nom)}`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!copies.length) return null;
  return (
    <div className="mt-5" data-testid="sauvegarde-copies">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Copies de sécurité faites avant une restauration</p>
      {copies.map((c, i) => (
        <div key={c.nom} className="flex items-center gap-2 border-t border-slate-100 py-1.5 text-sm" data-testid={`sauvegarde-copie-${i}`}>
          <span className="min-w-0 flex-1 truncate">{c.nom}</span>
          <span className="text-xs text-slate-500">{taille(c.taille)}</span>
          <Button size="icon" variant="ghost" className="h-8 w-8" title="Télécharger" onClick={() => download(`/local/copies/${encodeURIComponent(c.nom)}`, {}, c.nom, false).catch((e) => toast.error(errMsg(e)))} data-testid={`sauvegarde-copie-${i}-telecharger`}><Download className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" title="Supprimer" onClick={() => supprimer(c.nom)} data-testid={`sauvegarde-copie-${i}-supprimer`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
    </div>
  );
};

export const SauvegardePanel = () => {
  const { signOut } = useAuth();
  const ref = useRef(null);
  const [copies, setCopies] = useState([]);
  const [fichier, setFichier] = useState(null);
  const [envoi, setEnvoi] = useState("");
  const [progres, setProgres] = useState(0);
  const charger = useCallback(() => api.get("/local/copies").then((r) => setCopies(r.data)).catch(() => {}), []);
  useEffect(() => { charger(); }, [charger]);
  const sauvegarder = async () => {
    setEnvoi("sauvegarde");
    try { await download("/local/sauvegarde", {}, "sauvegarde_rapprochement.zip", false); toast.success("Sauvegarde téléchargée : copiez-la sur une clé USB ou un disque externe"); }
    catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(""); }
  };
  const restaurer = async () => {
    const fd = new FormData();
    fd.append("file", fichier);
    setFichier(null); setEnvoi("restauration"); setProgres(0);
    try {
      const { data } = await api.post("/local/restaurer", fd, { onUploadProgress: (p) => p.total && setProgres(Math.round((p.loaded / p.total) * 100)) });
      toast.success(`Restauration terminée (${data.documents} éléments). Copie de sécurité : ${data.copie}. Reconnectez-vous.`);
      await signOut();
      window.location.href = "/login";
    } catch (e) { toast.error(errMsg(e)); charger(); } finally { setEnvoi(""); ref.current.value = ""; }
  };
  return (
    <Panel title="Sauvegarde et restauration" testId="sauvegarde-panel">
      <p className="mb-4 text-xs text-slate-500">La sauvegarde contient toutes vos données : réservations, opérations, réglages, comptes et fichiers importés. Restaurer remplace tout par le contenu de la sauvegarde ; une copie de sécurité des données actuelles est faite juste avant.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={sauvegarder} disabled={!!envoi} className="bg-emerald-700 hover:bg-emerald-800" data-testid="sauvegarde-telecharger">
          {envoi === "sauvegarde" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Faire une sauvegarde
        </Button>
        <input ref={ref} type="file" accept=".zip" className="hidden" onChange={(e) => setFichier(e.target.files[0] || null)} data-testid="sauvegarde-fichier" />
        <Button variant="outline" disabled={!!envoi} onClick={() => ref.current.click()} data-testid="sauvegarde-restaurer">
          {envoi === "restauration" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}Restaurer une sauvegarde
        </Button>
      </div>
      {envoi === "restauration" && <div className="mt-3 max-w-sm" data-testid="sauvegarde-progres"><Progress value={progres} /><p className="mt-1 text-xs text-slate-500">{progres < 100 ? `Envoi ${progres} %` : "Restauration en cours…"}</p></div>}
      <Copies copies={copies} onChange={charger} />
      <AlertDialog open={!!fichier} onOpenChange={(o) => { if (!o) { setFichier(null); ref.current.value = ""; } }}>
        <AlertDialogContent data-testid="sauvegarde-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle>Remplacer toutes les données ?</AlertDialogTitle>
            <AlertDialogDescription>Les données actuelles seront remplacées par celles de « {fichier?.name} ». Une copie de sécurité est faite d'abord. Vous devrez vous reconnecter avec le compte de la sauvegarde.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sauvegarde-annuler">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={restaurer} className="bg-red-600 hover:bg-red-700" data-testid="sauvegarde-confirmer">Restaurer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};
